/**
 * Generic device state store using OSC-style flat key-value paths.
 *
 * All device types are flattened into a uniform Record of paths → primitives.
 * Example paths:
 *   /model              → "Blackmagic Smart Videohub 20x20"
 *   /inputs/0/label     → "Camera 1"
 *   /outputs/3/source   → 2
 *   /channels/0/fader   → 0.75
 *   /channels/0/mute    → false
 */
import { dLiveToStore as dLiveValuesToStore, storeToDLiveState as storeValuesToDLiveState, } from "../dlive-store.js";
import { DLIVE_FADER_MIN_DB, DLIVE_PEQ_BANDS, } from "../dlive.js";
import { SS_BORDER_LIB_FIELDS, ssBorderState } from "../supersource-render.js";
// ─── Conversion: typed → store ───────────────────────────────
function videoHubToStore(s) {
    const values = {};
    values["/model"] = s.model;
    for (const inp of s.inputs) {
        values[`/inputs/${inp.index}/label`] = inp.label;
    }
    for (const out of s.outputs) {
        values[`/outputs/${out.index}/label`] = out.label;
        values[`/outputs/${out.index}/source`] = out.source;
        if (out.lock)
            values[`/outputs/${out.index}/lock`] = out.lock;
    }
    return { values };
}
function atemDynamicsToStore(values, prefix, d) {
    values[`${prefix}/expanderEnabled`] = d.expanderEnabled;
    values[`${prefix}/expanderThreshold`] = d.expanderThreshold;
    values[`${prefix}/expanderRange`] = d.expanderRange;
    values[`${prefix}/expanderRatio`] = d.expanderRatio;
    values[`${prefix}/expanderAttack`] = d.expanderAttack;
    values[`${prefix}/expanderHold`] = d.expanderHold;
    values[`${prefix}/expanderRelease`] = d.expanderRelease;
    values[`${prefix}/compressorEnabled`] = d.compressorEnabled;
    values[`${prefix}/compressorThreshold`] = d.compressorThreshold;
    values[`${prefix}/compressorRatio`] = d.compressorRatio;
    values[`${prefix}/compressorAttack`] = d.compressorAttack;
    values[`${prefix}/compressorHold`] = d.compressorHold;
    values[`${prefix}/compressorRelease`] = d.compressorRelease;
    values[`${prefix}/compressorMakeUpGain`] = d.compressorMakeUpGain;
    values[`${prefix}/limiterEnabled`] = d.limiterEnabled;
    values[`${prefix}/limiterThreshold`] = d.limiterThreshold;
    values[`${prefix}/limiterAttack`] = d.limiterAttack;
    values[`${prefix}/limiterHold`] = d.limiterHold;
    values[`${prefix}/limiterRelease`] = d.limiterRelease;
}
function atemEqToStore(values, prefix, bands) {
    for (let b = 0; b < bands.length; b++) {
        const bp = `${prefix}/${b}`;
        values[`${bp}/enabled`] = bands[b].enabled;
        values[`${bp}/bandType`] = bands[b].bandType;
        values[`${bp}/frequency`] = bands[b].frequency;
        values[`${bp}/gain`] = bands[b].gain;
        values[`${bp}/qFactor`] = bands[b].qFactor;
    }
}
function atemToStore(s) {
    const values = {};
    values["/model"] = s.model;
    // Inputs
    for (const inp of s.inputs) {
        values[`/inputs/${inp.index}/shortName`] = inp.shortName;
        values[`/inputs/${inp.index}/longName`] = inp.longName;
    }
    // Mix Effects
    for (let i = 0; i < s.mixEffects.length; i++) {
        const me = s.mixEffects[i];
        const p = `/mixEffects/${i}`;
        values[`${p}/programInput`] = me.programInput;
        values[`${p}/previewInput`] = me.previewInput;
        values[`${p}/inTransition`] = me.inTransition;
        values[`${p}/transitionPosition`] = me.transitionPosition;
        values[`${p}/transition/style`] = me.transitionSettings.style;
        values[`${p}/transition/rate`] = me.transitionSettings.rate;
        values[`${p}/transition/nextStyle`] = me.transitionSettings.nextStyle;
        values[`${p}/transition/nextRate`] = me.transitionSettings.nextRate;
        values[`${p}/fadeToBlack`] = me.fadeToBlack;
        values[`${p}/fadeToBlackRate`] = me.fadeToBlackRate;
        values[`${p}/fadeToBlackInTransition`] = me.fadeToBlackInTransition;
    }
    // Auxiliaries
    for (const aux of s.auxiliaries) {
        values[`/auxiliaries/${aux.index}/source`] = aux.source;
        values[`/auxiliaries/${aux.index}/shortName`] = aux.shortName;
        values[`/auxiliaries/${aux.index}/longName`] = aux.longName;
    }
    // Upstream Keyers
    for (const usk of s.upstreamKeyers) {
        const p = `/upstreamKeyers/${usk.index}`;
        values[`${p}/onAir`] = usk.onAir;
        values[`${p}/type`] = usk.type;
        values[`${p}/fillSource`] = usk.fillSource;
        values[`${p}/keySource`] = usk.keySource;
        values[`${p}/maskEnabled`] = usk.maskEnabled;
        values[`${p}/maskTop`] = usk.maskTop;
        values[`${p}/maskBottom`] = usk.maskBottom;
        values[`${p}/maskLeft`] = usk.maskLeft;
        values[`${p}/maskRight`] = usk.maskRight;
        values[`${p}/lumaPreMultiplied`] = usk.lumaPreMultiplied;
        values[`${p}/lumaClip`] = usk.lumaClip;
        values[`${p}/lumaGain`] = usk.lumaGain;
        values[`${p}/lumaInvert`] = usk.lumaInvert;
        values[`${p}/chromaHue`] = usk.chromaHue;
        values[`${p}/chromaGain`] = usk.chromaGain;
        values[`${p}/chromaYSuppress`] = usk.chromaYSuppress;
        values[`${p}/chromaLift`] = usk.chromaLift;
        values[`${p}/chromaNarrow`] = usk.chromaNarrow;
        values[`${p}/dveSizeX`] = usk.dveSizeX;
        values[`${p}/dveSizeY`] = usk.dveSizeY;
        values[`${p}/dvePositionX`] = usk.dvePositionX;
        values[`${p}/dvePositionY`] = usk.dvePositionY;
        values[`${p}/dveRotation`] = usk.dveRotation;
        values[`${p}/dveBorderEnabled`] = usk.dveBorderEnabled;
        values[`${p}/dveShadowEnabled`] = usk.dveShadowEnabled;
    }
    // Downstream Keyers
    for (const dsk of s.downstreamKeyers) {
        const p = `/downstreamKeyers/${dsk.index}`;
        values[`${p}/onAir`] = dsk.onAir;
        values[`${p}/tie`] = dsk.tie;
        values[`${p}/fillSource`] = dsk.fillSource;
        values[`${p}/keySource`] = dsk.keySource;
        values[`${p}/rate`] = dsk.rate;
        values[`${p}/preMultiplied`] = dsk.preMultiplied;
        values[`${p}/clip`] = dsk.clip;
        values[`${p}/gain`] = dsk.gain;
        values[`${p}/invert`] = dsk.invert;
        values[`${p}/maskEnabled`] = dsk.maskEnabled;
        values[`${p}/maskTop`] = dsk.maskTop;
        values[`${p}/maskBottom`] = dsk.maskBottom;
        values[`${p}/maskLeft`] = dsk.maskLeft;
        values[`${p}/maskRight`] = dsk.maskRight;
        values[`${p}/inTransition`] = dsk.inTransition;
        values[`${p}/isAuto`] = dsk.isAuto;
        values[`${p}/remainingFrames`] = dsk.remainingFrames;
    }
    // Color Generators
    for (const cg of s.colorGenerators) {
        const p = `/colorGenerators/${cg.index}`;
        values[`${p}/hue`] = cg.hue;
        values[`${p}/saturation`] = cg.saturation;
        values[`${p}/luminance`] = cg.luminance;
    }
    // Audio
    const a = s.audio;
    for (const ch of a.channels) {
        const p = `/audio/channels/${ch.index}`;
        values[`${p}/sourceType`] = ch.sourceType;
        values[`${p}/mixOption`] = ch.mixOption;
        values[`${p}/gain`] = ch.gain;
        values[`${p}/balance`] = ch.balance;
        values[`${p}/leftLevel`] = ch.leftLevel;
        values[`${p}/rightLevel`] = ch.rightLevel;
    }
    for (const fi of a.fairlightInputs) {
        const p = `/audio/fairlightInputs/${fi.index}`;
        values[`${p}/shortName`] = fi.shortName;
        values[`${p}/longName`] = fi.longName;
        values[`${p}/sourceType`] = fi.sourceType;
        values[`${p}/externalPortType`] = fi.externalPortType;
        values[`${p}/activeConfiguration`] = fi.activeConfiguration;
        for (let si = 0; si < fi.sources.length; si++) {
            const src = fi.sources[si];
            const sp = `${p}/sources/${si}`;
            values[`${sp}/sourceId`] = src.sourceId;
            values[`${sp}/sourceType`] = src.sourceType;
            values[`${sp}/gain`] = src.gain;
            values[`${sp}/balance`] = src.balance;
            values[`${sp}/faderGain`] = src.faderGain;
            values[`${sp}/mixOption`] = src.mixOption;
            values[`${sp}/leftLevel`] = src.leftLevel;
            values[`${sp}/rightLevel`] = src.rightLevel;
            atemEqToStore(values, `${sp}/eq`, src.eq);
            atemDynamicsToStore(values, `${sp}/dynamics`, src.dynamics);
        }
    }
    // Audio master
    const mp = `/audio/master`;
    values[`${mp}/gain`] = a.master.gain;
    values[`${mp}/leftLevel`] = a.master.leftLevel;
    values[`${mp}/rightLevel`] = a.master.rightLevel;
    values[`${mp}/followFadeToBlack`] = a.master.followFadeToBlack;
    atemEqToStore(values, `${mp}/eq`, a.master.eq);
    atemDynamicsToStore(values, `${mp}/dynamics`, a.master.dynamics);
    // Audio monitor / headphone
    values["/audio/monitorGain"] = a.monitorGain;
    values["/audio/monitorMute"] = a.monitorMute;
    values["/audio/monitorSolo"] = a.monitorSolo;
    values["/audio/monitorSoloInput"] = a.monitorSoloInput;
    values["/audio/headphoneGain"] = a.headphoneGain;
    values["/audio/headphoneMute"] = a.headphoneMute;
    // Audio routing
    for (const ro of a.audioRouting) {
        const rp = `/audio/routing/${ro.index}`;
        values[`${rp}/name`] = ro.name;
        values[`${rp}/sourceType`] = ro.sourceType;
        for (const cp of ro.channelPairs) {
            const cpp = `${rp}/channelPairs/${cp.pairIndex}`;
            values[`${cpp}/sourceInputIndex`] = cp.sourceInputIndex;
            values[`${cpp}/sourceId`] = cp.sourceId;
            values[`${cpp}/enabled`] = cp.enabled;
        }
    }
    // Media Players
    for (const mp of s.mediaPlayers) {
        const p = `/mediaPlayers/${mp.index}`;
        values[`${p}/sourceType`] = mp.sourceType;
        values[`${p}/sourceIndex`] = mp.sourceIndex;
        values[`${p}/playing`] = mp.playing;
        values[`${p}/loop`] = mp.loop;
        values[`${p}/atBeginning`] = mp.atBeginning;
        values[`${p}/clipFrameCount`] = mp.clipFrameCount;
    }
    // Media Pool
    for (const still of s.mediaPool.stills) {
        const p = `/mediaPool/stills/${still.index}`;
        values[`${p}/isUsed`] = still.isUsed;
        values[`${p}/name`] = still.name;
    }
    for (const clip of s.mediaPool.clips) {
        const p = `/mediaPool/clips/${clip.index}`;
        values[`${p}/isUsed`] = clip.isUsed;
        values[`${p}/name`] = clip.name;
        values[`${p}/frameCount`] = clip.frameCount;
    }
    // Super Sources
    for (const ss of s.superSources) {
        const p = `/superSources/${ss.index}`;
        values[`${p}/artFillSource`] = ss.artFillSource;
        values[`${p}/artKeySource`] = ss.artKeySource;
        if (ss.artOption !== undefined) {
            values[`${p}/artOption`] = ss.artOption;
        }
        values[`${p}/artPreMultiplied`] = ss.artPreMultiplied;
        values[`${p}/artClip`] = ss.artClip;
        values[`${p}/artGain`] = ss.artGain;
        values[`${p}/artInvertKey`] = ss.artInvertKey;
        values[`${p}/borderEnabled`] = ss.borderEnabled;
        // Full per-SuperSource border, atem-connection field names (writable)
        if (ss.border) {
            for (const [field, name] of Object.entries(SS_BORDER_LIB_FIELDS)) {
                values[`${p}/${name}`] = ss.border[field];
            }
        }
        for (const box of ss.boxes) {
            const bp = `${p}/boxes/${box.index}`;
            values[`${bp}/enabled`] = box.enabled;
            values[`${bp}/source`] = box.source;
            values[`${bp}/x`] = box.x;
            values[`${bp}/y`] = box.y;
            values[`${bp}/size`] = box.size;
            values[`${bp}/cropped`] = box.cropped;
            values[`${bp}/cropTop`] = box.cropTop;
            values[`${bp}/cropBottom`] = box.cropBottom;
            values[`${bp}/cropLeft`] = box.cropLeft;
            values[`${bp}/cropRight`] = box.cropRight;
        }
    }
    // Macros
    for (const m of s.macros) {
        const p = `/macros/${m.index}`;
        values[`${p}/isUsed`] = m.isUsed;
        values[`${p}/name`] = m.name;
        values[`${p}/description`] = m.description;
        values[`${p}/isRunning`] = m.isRunning;
        values[`${p}/isWaiting`] = m.isWaiting;
        values[`${p}/isRecording`] = m.isRecording;
    }
    // Multi Views
    for (const mv of s.multiViews) {
        const p = `/multiViews/${mv.index}`;
        values[`${p}/layout`] = mv.layout;
        values[`${p}/programPreviewSwapped`] = mv.programPreviewSwapped;
        values[`${p}/vuMetersEnabled`] = mv.vuMetersEnabled;
        for (const w of mv.windows) {
            const wp = `${p}/windows/${w.index}`;
            values[`${wp}/source`] = w.source;
            values[`${wp}/safeAreaEnabled`] = w.safeAreaEnabled;
            values[`${wp}/audioMeterEnabled`] = w.audioMeterEnabled;
        }
    }
    return { values };
}
function wingToStore(s) {
    const values = {};
    values["/mainFader"] = s.mainFader;
    values["/mainMute"] = s.mainMute;
    for (const ch of s.channels) {
        values[`/channels/${ch.index}/name`] = ch.name;
        values[`/channels/${ch.index}/fader`] = ch.fader;
        values[`/channels/${ch.index}/mute`] = ch.mute;
    }
    for (const bus of s.buses) {
        values[`/buses/${bus.index}/name`] = bus.name;
        values[`/buses/${bus.index}/fader`] = bus.fader;
        values[`/buses/${bus.index}/mute`] = bus.mute;
    }
    return { values };
}
function dLiveToStore(s) {
    return { values: dLiveValuesToStore(s) };
}
function companionToStore(s) {
    const values = {};
    values["/pages"] = s.pages;
    values["/buttonsPerPage"] = s.buttonsPerPage;
    for (let i = 0; i < s.buttons.length; i++) {
        const btn = s.buttons[i];
        values[`/buttons/${i}/page`] = btn.page;
        values[`/buttons/${i}/bank`] = btn.bank;
        values[`/buttons/${i}/text`] = btn.text;
    }
    return { values };
}
export function deviceStateToStore(ds) {
    switch (ds.type) {
        case "videohub":
            return videoHubToStore(ds.state);
        case "atem":
            return atemToStore(ds.state);
        case "behringer_wing":
            return wingToStore(ds.state);
        case "dlive":
            return dLiveToStore(ds.state);
        case "companion":
            return companionToStore(ds.state);
        case "obe_zixi":
            return obeZixiToStore(ds.state);
        case "ultra_encode":
            return ultraEncodeToStore(ds.state);
        // Runtime status only, nothing a config stores or loads
        case "liveu_server":
        case "generic":
            return { values: {} };
    }
}
/** Monitoring snapshot only: the OBE/Zixi device is not driven by paths. */
function obeZixiToStore(s) {
    const v = {
        "/phase": s.phase,
        "/encoder/running": s.encoder.running,
        "/encoder/input_active": s.encoder.input_active,
        "/zixi/output/on": s.zixi.output?.on ?? false,
        "/zixi/output/status": s.zixi.output?.status ?? "",
    };
    if (s.target) {
        v["/target/host"] = s.target.host;
        v["/target/port"] = s.target.port;
        if (s.target.stream_id)
            v["/target/stream_id"] = s.target.stream_id;
    }
    s.zixi.output?.links.forEach((link, i) => {
        v[`/zixi/links/${i}/name`] = link.name;
        v[`/zixi/links/${i}/status`] = link.status;
        if (link.source_ip)
            v[`/zixi/links/${i}/source_ip`] = link.source_ip;
    });
    return { values: v };
}
/** Monitoring snapshot only: the Ultra Encode is not driven by paths. */
function ultraEncodeToStore(s) {
    const v = {
        "/phase": s.phase,
        "/live": s.live,
        "/recording": s.recording,
        "/input/signal": s.input.signal,
    };
    for (const output of s.outputs) {
        v[`/outputs/${output.id}/name`] = output.name;
        v[`/outputs/${output.id}/enabled`] = output.enabled;
        v[`/outputs/${output.id}/status`] = output.status;
        if (output.host)
            v[`/outputs/${output.id}/host`] = output.host;
        if (output.port !== null)
            v[`/outputs/${output.id}/port`] = output.port;
    }
    return { values: v };
}
// ─── Conversion: store → typed ───────────────────────────────
export function storeToVideoHubState(store) {
    const v = store.values;
    const model = String(v["/model"] ?? "VideoHub");
    // Collect inputs
    const inputIndices = new Set();
    const outputIndices = new Set();
    for (const key of Object.keys(v)) {
        const inputMatch = key.match(/^\/inputs\/(\d+)\//);
        if (inputMatch)
            inputIndices.add(Number(inputMatch[1]));
        const outputMatch = key.match(/^\/outputs\/(\d+)\//);
        if (outputMatch)
            outputIndices.add(Number(outputMatch[1]));
    }
    const inputs = [...inputIndices]
        .sort((a, b) => a - b)
        .map((i) => ({
        index: i,
        label: String(v[`/inputs/${i}/label`] ?? `Input ${i + 1}`),
    }));
    const outputs = [...outputIndices]
        .sort((a, b) => a - b)
        .map((i) => {
        const lock = v[`/outputs/${i}/lock`];
        return {
            index: i,
            label: String(v[`/outputs/${i}/label`] ?? `Output ${i + 1}`),
            source: Number(v[`/outputs/${i}/source`] ?? i),
            ...(lock === "owned" || lock === "locked" || lock === "unlocked"
                ? { lock: lock }
                : {}),
        };
    });
    return { model, inputs, outputs };
}
export function storeToWingState(store) {
    const v = store.values;
    const channelIndices = new Set();
    const busIndices = new Set();
    for (const key of Object.keys(v)) {
        const chMatch = key.match(/^\/channels\/(\d+)\//);
        if (chMatch)
            channelIndices.add(Number(chMatch[1]));
        const busMatch = key.match(/^\/buses\/(\d+)\//);
        if (busMatch)
            busIndices.add(Number(busMatch[1]));
    }
    return {
        channels: [...channelIndices]
            .sort((a, b) => a - b)
            .map((i) => ({
            index: i,
            name: String(v[`/channels/${i}/name`] ?? `Ch ${i + 1}`),
            fader: Number(v[`/channels/${i}/fader`] ?? 0),
            mute: Boolean(v[`/channels/${i}/mute`] ?? false),
        })),
        buses: [...busIndices]
            .sort((a, b) => a - b)
            .map((i) => ({
            index: i,
            name: String(v[`/buses/${i}/name`] ?? `Bus ${i + 1}`),
            fader: Number(v[`/buses/${i}/fader`] ?? 0),
            mute: Boolean(v[`/buses/${i}/mute`] ?? false),
        })),
        mainFader: Number(v["/mainFader"] ?? 0),
        mainMute: Boolean(v["/mainMute"] ?? false),
    };
}
export function storeToDLiveState(store) {
    return storeValuesToDLiveState(store.values);
}
export function storeToCompanionState(store) {
    const v = store.values;
    const buttonIndices = new Set();
    for (const key of Object.keys(v)) {
        const match = key.match(/^\/buttons\/(\d+)\//);
        if (match)
            buttonIndices.add(Number(match[1]));
    }
    return {
        pages: Number(v["/pages"] ?? 1),
        buttonsPerPage: Number(v["/buttonsPerPage"] ?? 32),
        buttons: [...buttonIndices]
            .sort((a, b) => a - b)
            .map((i) => ({
            page: Number(v[`/buttons/${i}/page`] ?? 1),
            bank: Number(v[`/buttons/${i}/bank`] ?? i),
            text: String(v[`/buttons/${i}/text`] ?? ""),
            pressed: false,
        })),
    };
}
// Helper: collect unique numeric indices from store keys matching a prefix pattern
function collectIndices(v, pattern) {
    const indices = new Set();
    for (const key of Object.keys(v)) {
        const m = key.match(pattern);
        if (m)
            indices.add(Number(m[1]));
    }
    return [...indices].sort((a, b) => a - b);
}
function storeToDynamics(v, prefix) {
    return {
        expanderEnabled: Boolean(v[`${prefix}/expanderEnabled`] ?? false),
        expanderThreshold: Number(v[`${prefix}/expanderThreshold`] ?? -40),
        expanderRange: Number(v[`${prefix}/expanderRange`] ?? 0),
        expanderRatio: Number(v[`${prefix}/expanderRatio`] ?? 1),
        expanderAttack: Number(v[`${prefix}/expanderAttack`] ?? 5),
        expanderHold: Number(v[`${prefix}/expanderHold`] ?? 0),
        expanderRelease: Number(v[`${prefix}/expanderRelease`] ?? 50),
        compressorEnabled: Boolean(v[`${prefix}/compressorEnabled`] ?? false),
        compressorThreshold: Number(v[`${prefix}/compressorThreshold`] ?? 0),
        compressorRatio: Number(v[`${prefix}/compressorRatio`] ?? 1),
        compressorAttack: Number(v[`${prefix}/compressorAttack`] ?? 5),
        compressorHold: Number(v[`${prefix}/compressorHold`] ?? 0),
        compressorRelease: Number(v[`${prefix}/compressorRelease`] ?? 50),
        compressorMakeUpGain: Number(v[`${prefix}/compressorMakeUpGain`] ?? 0),
        limiterEnabled: Boolean(v[`${prefix}/limiterEnabled`] ?? false),
        limiterThreshold: Number(v[`${prefix}/limiterThreshold`] ?? 0),
        limiterAttack: Number(v[`${prefix}/limiterAttack`] ?? 0.7),
        limiterHold: Number(v[`${prefix}/limiterHold`] ?? 0),
        limiterRelease: Number(v[`${prefix}/limiterRelease`] ?? 50),
    };
}
function storeToEqBands(v, prefix) {
    const indices = collectIndices(v, new RegExp(`^${prefix.replace(/[/]/g, "\\/")}\\/(\\d+)\\/`));
    return indices.map((b) => ({
        enabled: Boolean(v[`${prefix}/${b}/enabled`] ?? false),
        bandType: String(v[`${prefix}/${b}/bandType`] ?? "bell"),
        frequency: Number(v[`${prefix}/${b}/frequency`] ?? 1000),
        gain: Number(v[`${prefix}/${b}/gain`] ?? 0),
        qFactor: Number(v[`${prefix}/${b}/qFactor`] ?? 1),
    }));
}
export function storeToAtemState(store) {
    const v = store.values;
    const model = String(v["/model"] ?? "ATEM");
    // Inputs
    const inputIndices = collectIndices(v, /^\/inputs\/(\d+)\//);
    const inputs = inputIndices.map((i) => ({
        index: i,
        shortName: String(v[`/inputs/${i}/shortName`] ?? `In${i + 1}`),
        longName: String(v[`/inputs/${i}/longName`] ?? `Input ${i + 1}`),
    }));
    // Mix Effects
    const meIndices = collectIndices(v, /^\/mixEffects\/(\d+)\//);
    const mixEffects = meIndices.map((i) => {
        const p = `/mixEffects/${i}`;
        return {
            programInput: Number(v[`${p}/programInput`] ?? 0),
            previewInput: Number(v[`${p}/previewInput`] ?? 1),
            inTransition: Boolean(v[`${p}/inTransition`] ?? false),
            transitionPosition: Number(v[`${p}/transitionPosition`] ?? 0),
            transitionSettings: {
                style: String(v[`${p}/transition/style`] ?? "mix"),
                rate: Number(v[`${p}/transition/rate`] ?? 30),
                nextStyle: String(v[`${p}/transition/nextStyle`] ?? "mix"),
                nextRate: Number(v[`${p}/transition/nextRate`] ?? 30),
            },
            fadeToBlack: Boolean(v[`${p}/fadeToBlack`] ?? false),
            fadeToBlackRate: Number(v[`${p}/fadeToBlackRate`] ?? 30),
            fadeToBlackInTransition: Boolean(v[`${p}/fadeToBlackInTransition`] ?? false),
        };
    });
    // Auxiliaries
    const auxIndices = collectIndices(v, /^\/auxiliaries\/(\d+)\//);
    const auxiliaries = auxIndices.map((i) => ({
        index: i,
        source: Number(v[`/auxiliaries/${i}/source`] ?? 0),
        shortName: String(v[`/auxiliaries/${i}/shortName`] ?? `Aux ${i + 1}`),
        longName: String(v[`/auxiliaries/${i}/longName`] ?? `Auxiliary Output ${i + 1}`),
    }));
    // Upstream Keyers
    const uskIndices = collectIndices(v, /^\/upstreamKeyers\/(\d+)\//);
    const upstreamKeyers = uskIndices.map((i) => {
        const p = `/upstreamKeyers/${i}`;
        return {
            index: i,
            onAir: Boolean(v[`${p}/onAir`] ?? false),
            type: String(v[`${p}/type`] ?? "luma"),
            fillSource: Number(v[`${p}/fillSource`] ?? 0),
            keySource: Number(v[`${p}/keySource`] ?? 0),
            maskEnabled: Boolean(v[`${p}/maskEnabled`] ?? false),
            maskTop: Number(v[`${p}/maskTop`] ?? 0),
            maskBottom: Number(v[`${p}/maskBottom`] ?? 0),
            maskLeft: Number(v[`${p}/maskLeft`] ?? 0),
            maskRight: Number(v[`${p}/maskRight`] ?? 0),
            lumaPreMultiplied: Boolean(v[`${p}/lumaPreMultiplied`] ?? false),
            lumaClip: Number(v[`${p}/lumaClip`] ?? 0),
            lumaGain: Number(v[`${p}/lumaGain`] ?? 0),
            lumaInvert: Boolean(v[`${p}/lumaInvert`] ?? false),
            chromaHue: Number(v[`${p}/chromaHue`] ?? 0),
            chromaGain: Number(v[`${p}/chromaGain`] ?? 0),
            chromaYSuppress: Number(v[`${p}/chromaYSuppress`] ?? 0),
            chromaLift: Number(v[`${p}/chromaLift`] ?? 0),
            chromaNarrow: Boolean(v[`${p}/chromaNarrow`] ?? false),
            dveSizeX: Number(v[`${p}/dveSizeX`] ?? 0),
            dveSizeY: Number(v[`${p}/dveSizeY`] ?? 0),
            dvePositionX: Number(v[`${p}/dvePositionX`] ?? 0),
            dvePositionY: Number(v[`${p}/dvePositionY`] ?? 0),
            dveRotation: Number(v[`${p}/dveRotation`] ?? 0),
            dveBorderEnabled: Boolean(v[`${p}/dveBorderEnabled`] ?? false),
            dveShadowEnabled: Boolean(v[`${p}/dveShadowEnabled`] ?? false),
        };
    });
    // Downstream Keyers
    const dskIndices = collectIndices(v, /^\/downstreamKeyers\/(\d+)\//);
    const downstreamKeyers = dskIndices.map((i) => {
        const p = `/downstreamKeyers/${i}`;
        return {
            index: i,
            onAir: Boolean(v[`${p}/onAir`] ?? false),
            tie: Boolean(v[`${p}/tie`] ?? false),
            fillSource: Number(v[`${p}/fillSource`] ?? 0),
            keySource: Number(v[`${p}/keySource`] ?? 0),
            rate: Number(v[`${p}/rate`] ?? 30),
            preMultiplied: Boolean(v[`${p}/preMultiplied`] ?? false),
            clip: Number(v[`${p}/clip`] ?? 0),
            gain: Number(v[`${p}/gain`] ?? 0),
            invert: Boolean(v[`${p}/invert`] ?? false),
            maskEnabled: Boolean(v[`${p}/maskEnabled`] ?? false),
            maskTop: Number(v[`${p}/maskTop`] ?? 0),
            maskBottom: Number(v[`${p}/maskBottom`] ?? 0),
            maskLeft: Number(v[`${p}/maskLeft`] ?? 0),
            maskRight: Number(v[`${p}/maskRight`] ?? 0),
            inTransition: Boolean(v[`${p}/inTransition`] ?? false),
            isAuto: Boolean(v[`${p}/isAuto`] ?? false),
            remainingFrames: Number(v[`${p}/remainingFrames`] ?? 0),
        };
    });
    // Color Generators
    const cgIndices = collectIndices(v, /^\/colorGenerators\/(\d+)\//);
    const colorGenerators = cgIndices.map((i) => ({
        index: i,
        hue: Number(v[`/colorGenerators/${i}/hue`] ?? 0),
        saturation: Number(v[`/colorGenerators/${i}/saturation`] ?? 0),
        luminance: Number(v[`/colorGenerators/${i}/luminance`] ?? 0),
    }));
    // Audio channels
    const audioChIndices = collectIndices(v, /^\/audio\/channels\/(\d+)\//);
    const audioChannels = audioChIndices.map((i) => {
        const p = `/audio/channels/${i}`;
        return {
            index: i,
            sourceType: String(v[`${p}/sourceType`] ?? "external"),
            mixOption: String(v[`${p}/mixOption`] ?? "off"),
            gain: Number(v[`${p}/gain`] ?? 0),
            balance: Number(v[`${p}/balance`] ?? 0),
            leftLevel: Number(v[`${p}/leftLevel`] ?? 0),
            rightLevel: Number(v[`${p}/rightLevel`] ?? 0),
        };
    });
    // Fairlight inputs
    const flIndices = collectIndices(v, /^\/audio\/fairlightInputs\/(\d+)\//);
    const fairlightInputs = flIndices.map((i) => {
        const p = `/audio/fairlightInputs/${i}`;
        const srcIndices = collectIndices(v, new RegExp(`^\\/audio\\/fairlightInputs\\/${i}\\/sources\\/(\\d+)\\/`));
        const sources = srcIndices.map((si) => {
            const sp = `${p}/sources/${si}`;
            return {
                sourceId: Number(v[`${sp}/sourceId`] ?? si),
                sourceType: String(v[`${sp}/sourceType`] ?? "stereo"),
                gain: Number(v[`${sp}/gain`] ?? 0),
                balance: Number(v[`${sp}/balance`] ?? 0),
                faderGain: Number(v[`${sp}/faderGain`] ?? 0),
                mixOption: String(v[`${sp}/mixOption`] ?? "off"),
                leftLevel: Number(v[`${sp}/leftLevel`] ?? 0),
                rightLevel: Number(v[`${sp}/rightLevel`] ?? 0),
                eq: storeToEqBands(v, `${sp}/eq`),
                dynamics: storeToDynamics(v, `${sp}/dynamics`),
            };
        });
        return {
            index: i,
            shortName: String(v[`${p}/shortName`] ?? `In ${i}`),
            longName: String(v[`${p}/longName`] ?? `Input ${i}`),
            sourceType: String(v[`${p}/sourceType`] ?? "external"),
            externalPortType: String(v[`${p}/externalPortType`] ?? "sdi"),
            activeConfiguration: String(v[`${p}/activeConfiguration`] ?? "stereo"),
            supportedConfigurations: ["mono", "stereo"],
            sources,
        };
    });
    // Audio routing
    const routeIndices = collectIndices(v, /^\/audio\/routing\/(\d+)\//);
    const audioRouting = routeIndices.map((i) => {
        const rp = `/audio/routing/${i}`;
        const pairIndices = collectIndices(v, new RegExp(`^\\/audio\\/routing\\/${i}\\/channelPairs\\/(\\d+)\\/`));
        const channelPairs = pairIndices.map((pi) => {
            const cpp = `${rp}/channelPairs/${pi}`;
            return {
                pairIndex: pi,
                sourceInputIndex: Number(v[`${cpp}/sourceInputIndex`] ?? -1),
                sourceId: Number(v[`${cpp}/sourceId`] ?? -1),
                enabled: Boolean(v[`${cpp}/enabled`] ?? false),
            };
        });
        return {
            index: i,
            name: String(v[`${rp}/name`] ?? `Output ${i}`),
            sourceType: String(v[`${rp}/sourceType`] ?? "program"),
            channelPairs,
        };
    });
    // Audio master
    const masterP = "/audio/master";
    const master = {
        gain: Number(v[`${masterP}/gain`] ?? 0),
        leftLevel: Number(v[`${masterP}/leftLevel`] ?? 0),
        rightLevel: Number(v[`${masterP}/rightLevel`] ?? 0),
        followFadeToBlack: Boolean(v[`${masterP}/followFadeToBlack`] ?? false),
        eq: storeToEqBands(v, `${masterP}/eq`),
        dynamics: storeToDynamics(v, `${masterP}/dynamics`),
    };
    const audio = {
        channels: audioChannels,
        fairlightInputs,
        master,
        monitorGain: Number(v["/audio/monitorGain"] ?? 0),
        monitorMute: Boolean(v["/audio/monitorMute"] ?? false),
        monitorSolo: Boolean(v["/audio/monitorSolo"] ?? false),
        monitorSoloInput: Number(v["/audio/monitorSoloInput"] ?? -1),
        audioRouting,
        headphoneGain: Number(v["/audio/headphoneGain"] ?? 0),
        headphoneMute: Boolean(v["/audio/headphoneMute"] ?? false),
    };
    // Media Players
    const mpIndices = collectIndices(v, /^\/mediaPlayers\/(\d+)\//);
    const mediaPlayers = mpIndices.map((i) => {
        const p = `/mediaPlayers/${i}`;
        return {
            index: i,
            sourceType: String(v[`${p}/sourceType`] ?? "still"),
            sourceIndex: Number(v[`${p}/sourceIndex`] ?? 0),
            playing: Boolean(v[`${p}/playing`] ?? false),
            loop: Boolean(v[`${p}/loop`] ?? false),
            atBeginning: Boolean(v[`${p}/atBeginning`] ?? true),
            clipFrameCount: Number(v[`${p}/clipFrameCount`] ?? 0),
        };
    });
    // Media Pool
    const stillIndices = collectIndices(v, /^\/mediaPool\/stills\/(\d+)\//);
    const stills = stillIndices.map((i) => ({
        index: i,
        isUsed: Boolean(v[`/mediaPool/stills/${i}/isUsed`] ?? false),
        name: String(v[`/mediaPool/stills/${i}/name`] ?? ""),
    }));
    const clipIndices = collectIndices(v, /^\/mediaPool\/clips\/(\d+)\//);
    const clips = clipIndices.map((i) => ({
        index: i,
        isUsed: Boolean(v[`/mediaPool/clips/${i}/isUsed`] ?? false),
        name: String(v[`/mediaPool/clips/${i}/name`] ?? ""),
        frameCount: Number(v[`/mediaPool/clips/${i}/frameCount`] ?? 0),
    }));
    const mediaPool = { stills, clips };
    // Super Sources
    const ssIndices = collectIndices(v, /^\/superSources\/(\d+)\//);
    const superSources = ssIndices.map((i) => {
        const p = `/superSources/${i}`;
        const boxIndices = collectIndices(v, new RegExp(`^\\/superSources\\/${i}\\/boxes\\/(\\d+)\\/`));
        const boxes = boxIndices.map((bi) => {
            const bp = `${p}/boxes/${bi}`;
            return {
                index: bi,
                enabled: Boolean(v[`${bp}/enabled`] ?? false),
                source: Number(v[`${bp}/source`] ?? 0),
                x: Number(v[`${bp}/x`] ?? 0),
                y: Number(v[`${bp}/y`] ?? 0),
                size: Number(v[`${bp}/size`] ?? 500),
                cropped: Boolean(v[`${bp}/cropped`] ?? false),
                cropTop: Number(v[`${bp}/cropTop`] ?? 0),
                cropBottom: Number(v[`${bp}/cropBottom`] ?? 0),
                cropLeft: Number(v[`${bp}/cropLeft`] ?? 0),
                cropRight: Number(v[`${bp}/cropRight`] ?? 0),
            };
        });
        return {
            index: i,
            boxes,
            artFillSource: Number(v[`${p}/artFillSource`] ?? 0),
            artKeySource: Number(v[`${p}/artKeySource`] ?? 0),
            artOption: v[`${p}/artOption`] === "background"
                ? "background"
                : v[`${p}/artOption`] === "foreground"
                    ? "foreground"
                    : undefined,
            artPreMultiplied: Boolean(v[`${p}/artPreMultiplied`] ?? false),
            artClip: Number(v[`${p}/artClip`] ?? 0),
            artGain: Number(v[`${p}/artGain`] ?? 0),
            artInvertKey: Boolean(v[`${p}/artInvertKey`] ?? false),
            borderEnabled: Boolean(v[`${p}/borderEnabled`] ?? false),
            ...storeBorder(v, p),
        };
    });
    // Macros
    const macroIndices = collectIndices(v, /^\/macros\/(\d+)\//);
    const macros = macroIndices.map((i) => ({
        index: i,
        isUsed: Boolean(v[`/macros/${i}/isUsed`] ?? false),
        name: String(v[`/macros/${i}/name`] ?? ""),
        description: String(v[`/macros/${i}/description`] ?? ""),
        isRunning: Boolean(v[`/macros/${i}/isRunning`] ?? false),
        isWaiting: Boolean(v[`/macros/${i}/isWaiting`] ?? false),
        isRecording: Boolean(v[`/macros/${i}/isRecording`] ?? false),
    }));
    // Multi Views
    const mvIndices = collectIndices(v, /^\/multiViews\/(\d+)\//);
    const multiViews = mvIndices.map((i) => {
        const p = `/multiViews/${i}`;
        const winIndices = collectIndices(v, new RegExp(`^\\/multiViews\\/${i}\\/windows\\/(\\d+)\\/`));
        const windows = winIndices.map((wi) => {
            const wp = `${p}/windows/${wi}`;
            return {
                index: wi,
                source: Number(v[`${wp}/source`] ?? 0),
                safeAreaEnabled: Boolean(v[`${wp}/safeAreaEnabled`] ?? false),
                audioMeterEnabled: Boolean(v[`${wp}/audioMeterEnabled`] ?? false),
            };
        });
        return {
            index: i,
            layout: Number(v[`${p}/layout`] ?? 0),
            programPreviewSwapped: Boolean(v[`${p}/programPreviewSwapped`] ?? false),
            windows,
            vuMetersEnabled: Boolean(v[`${p}/vuMetersEnabled`] ?? false),
        };
    });
    return {
        model,
        inputs,
        mixEffects,
        auxiliaries,
        upstreamKeyers,
        downstreamKeyers,
        colorGenerators,
        audio,
        mediaPlayers,
        mediaPool,
        superSources,
        macros,
        multiViews,
    };
}
// ─── Default store creation ──────────────────────────────────
/**
 * A dLive to edit offline: `inputs` inputs, 8 stereo groups and aux, 4 stereo
 * matrices, 2 stereo FX sends and returns, the main LR, 24 DCAs and 8 mute
 * groups, every input sending to every aux.
 */
function defaultDLiveState(inputs) {
    const counts = {
        input: inputs,
        stereoGroup: 8,
        stereoAux: 8,
        stereoMatrix: 4,
        stereoFxSend: 2,
        fxReturn: 2,
        main: 1,
        dca: 24,
        muteGroup: 8,
    };
    const channels = [];
    for (const [type, count] of Object.entries(counts)) {
        for (let index = 0; index < count; index++) {
            if (type === "muteGroup") {
                channels.push({ type, index, mute: false });
                continue;
            }
            const strip = {
                type,
                index,
                name: type === "main" ? "LR" : `${index + 1}`,
                colour: "off",
                fader: type === "input" ? DLIVE_FADER_MIN_DB : 0,
                mute: false,
            };
            if (type === "input") {
                strip.mainAssign = true;
                strip.hpfOn = false;
                strip.hpfFrequency = 100;
                strip.peq = Array.from({ length: DLIVE_PEQ_BANDS }, (_, band) => ({
                    band,
                    type: "bell",
                    frequency: [100, 500, 2000, 8000][band],
                    width: 1,
                    gain: 0,
                }));
                strip.sends = Array.from({ length: counts.stereoAux ?? 0 }, (_, i) => ({
                    destType: "stereoAux",
                    destIndex: i,
                    level: DLIVE_FADER_MIN_DB,
                    on: true,
                }));
            }
            channels.push(strip);
        }
    }
    return {
        model: "dLive MixRack",
        midiChannel: 1,
        currentScene: 0,
        channels,
        preamps: [],
    };
}
export function createDefaultStore(connectionType, options) {
    const values = {};
    switch (connectionType) {
        case "videohub": {
            const inputs = options?.inputCount ?? 20;
            const outputs = options?.outputCount ?? 20;
            values["/model"] = options?.model ?? "VideoHub";
            for (let i = 0; i < inputs; i++) {
                values[`/inputs/${i}/label`] = `Input ${i + 1}`;
            }
            for (let i = 0; i < outputs; i++) {
                values[`/outputs/${i}/label`] = `Output ${i + 1}`;
                values[`/outputs/${i}/source`] = i < inputs ? i : 0;
            }
            break;
        }
        case "atem": {
            const inputs = options?.inputCount ?? 20;
            values["/model"] = options?.model ?? "ATEM";
            for (let i = 0; i < inputs; i++) {
                values[`/inputs/${i}/shortName`] = `In${i + 1}`;
                values[`/inputs/${i}/longName`] = `Input ${i + 1}`;
            }
            // 1 mix effect
            values["/mixEffects/0/programInput"] = 0;
            values["/mixEffects/0/previewInput"] = 1;
            values["/mixEffects/0/inTransition"] = false;
            values["/mixEffects/0/transitionPosition"] = 0;
            values["/mixEffects/0/transition/style"] = "mix";
            values["/mixEffects/0/transition/rate"] = 30;
            values["/mixEffects/0/transition/nextStyle"] = "mix";
            values["/mixEffects/0/transition/nextRate"] = 30;
            values["/mixEffects/0/fadeToBlack"] = false;
            values["/mixEffects/0/fadeToBlackRate"] = 30;
            values["/mixEffects/0/fadeToBlackInTransition"] = false;
            // 2 downstream keyers
            for (let i = 0; i < 2; i++) {
                const p = `/downstreamKeyers/${i}`;
                values[`${p}/onAir`] = false;
                values[`${p}/tie`] = false;
                values[`${p}/fillSource`] = 0;
                values[`${p}/keySource`] = 0;
                values[`${p}/rate`] = 30;
                values[`${p}/preMultiplied`] = false;
                values[`${p}/clip`] = 0;
                values[`${p}/gain`] = 0;
                values[`${p}/invert`] = false;
                values[`${p}/maskEnabled`] = false;
                values[`${p}/maskTop`] = 0;
                values[`${p}/maskBottom`] = 0;
                values[`${p}/maskLeft`] = 0;
                values[`${p}/maskRight`] = 0;
                values[`${p}/inTransition`] = false;
                values[`${p}/isAuto`] = false;
                values[`${p}/remainingFrames`] = 0;
            }
            // 2 color generators
            for (let i = 0; i < 2; i++) {
                values[`/colorGenerators/${i}/hue`] = 0;
                values[`/colorGenerators/${i}/saturation`] = 0;
                values[`/colorGenerators/${i}/luminance`] = 0;
            }
            // Audio defaults
            values["/audio/monitorGain"] = 0;
            values["/audio/monitorMute"] = false;
            values["/audio/monitorSolo"] = false;
            values["/audio/monitorSoloInput"] = -1;
            values["/audio/headphoneGain"] = 0;
            values["/audio/headphoneMute"] = false;
            values["/audio/master/gain"] = 0;
            values["/audio/master/leftLevel"] = 0;
            values["/audio/master/rightLevel"] = 0;
            values["/audio/master/followFadeToBlack"] = false;
            // 2 multiviews with 10 windows each
            for (let i = 0; i < 2; i++) {
                const p = `/multiViews/${i}`;
                values[`${p}/layout`] = 0;
                values[`${p}/programPreviewSwapped`] = false;
                values[`${p}/vuMetersEnabled`] = true;
                for (let w = 0; w < 10; w++) {
                    values[`${p}/windows/${w}/source`] = 0;
                    values[`${p}/windows/${w}/safeAreaEnabled`] = false;
                    values[`${p}/windows/${w}/audioMeterEnabled`] = false;
                }
            }
            break;
        }
        case "behringer_wing": {
            const channels = options?.channelCount ?? 32;
            const buses = options?.busCount ?? 16;
            values["/mainFader"] = 0.75;
            values["/mainMute"] = false;
            for (let i = 0; i < channels; i++) {
                values[`/channels/${i}/name`] = `Ch ${i + 1}`;
                values[`/channels/${i}/fader`] = 0.75;
                values[`/channels/${i}/mute`] = false;
            }
            for (let i = 0; i < buses; i++) {
                values[`/buses/${i}/name`] = `Bus ${i + 1}`;
                values[`/buses/${i}/fader`] = 0;
                values[`/buses/${i}/mute`] = false;
            }
            break;
        }
        case "dlive": {
            Object.assign(values, dLiveValuesToStore(defaultDLiveState(options?.channelCount ?? 32)));
            break;
        }
        case "companion": {
            const pages = options?.pageCount ?? 10;
            const bpp = options?.buttonsPerPage ?? 32;
            values["/pages"] = pages;
            values["/buttonsPerPage"] = bpp;
            break;
        }
        case "generic":
            break;
    }
    return { values };
}
/** The full SuperSource border from store values, when the store has it. */
function storeBorder(v, p) {
    const entries = Object.entries(SS_BORDER_LIB_FIELDS);
    if (!entries.some(([, name]) => v[`${p}/${name}`] !== undefined))
        return {};
    const border = {
        enabled: Boolean(v[`${p}/borderEnabled`] ?? false),
    };
    for (const [field, name] of entries) {
        const raw = v[`${p}/${name}`];
        if (raw !== undefined && raw !== null && raw !== "") {
            border[field] = Number(raw);
        }
    }
    return { border: ssBorderState(border) };
}
